import { useState } from 'react';
import { API_BASE_URL } from '../../api';

/** Owns the M-Pesa STK Push admin testing tool — send a test push, poll for
 * payment confirmation, keep a short history, and a separate connectivity check. */
export default function useStkTest({ showNotification }) {
  const [stkTestForm, setStkTestForm] = useState({
    phoneNumber: '',
    amount: 1,
    customerName: 'Test Customer',
    customerEmail: 'test@example.com',
  });
  const [stkTestLoading, setStkTestLoading] = useState(false);
  const [stkTestResult, setStkTestResult] = useState(null);
  const [stkTestHistory, setStkTestHistory] = useState([]);

  const handleStkTest = async (e) => {
    e.preventDefault();
    setStkTestLoading(true);
    setStkTestResult(null);

    try {
      const token = localStorage.getItem('adminToken');

      // Use a hardcoded product ID for STK testing (first available product)
      const testProduct = {
        id: 'cmgmqr07q0001cpweo7zgmszz',
        name: 'Neverfull MM Tote Bags',
        price: 3500,
      };

      const response = await fetch(`${API_BASE_URL}/api/mpesa/stkpush`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: stkTestForm.phoneNumber,
          amount: parseInt(stkTestForm.amount, 10),
          orderId: `ADMIN-TEST-${Date.now()}`,
          customerInfo: {
            fullName: stkTestForm.customerName,
            email: stkTestForm.customerEmail,
            phone: stkTestForm.phoneNumber,
          },
          cartItems: [{
            id: testProduct.id,
            productId: testProduct.id,
            quantity: 1,
            price: parseInt(stkTestForm.amount, 10),
            color: 'Test',
            colorCode: '#000000',
          }],
        }),
      });

      const result = await response.json();

      if (result.success) {
        const checkoutRequestID = result.checkoutRequestID;
        let paymentReceived = false;
        let attempts = 0;
        const maxAttempts = 30; // 30 seconds timeout

        setStkTestResult({
          success: false,
          message: 'STK Push sent! Waiting for payment confirmation...',
          checkoutRequestID: result.checkoutRequestID,
          orderId: result.orderId,
          formattedPhone: result.formattedPhone,
          productUsed: testProduct.name,
          status: 'waiting',
        });

        showNotification('STK Push sent! Please check your phone and enter M-Pesa PIN.', 'info');

        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const paymentResponse = await fetch(`${API_BASE_URL}/api/mpesa/check-payment/${checkoutRequestID}`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });

            if (paymentResponse.ok) {
              const paymentData = await paymentResponse.json();
              if (paymentData.success && paymentData.payment) {
                clearInterval(pollInterval);
                paymentReceived = true;

                setStkTestResult({
                  success: true,
                  message: 'Payment received successfully!',
                  checkoutRequestID: result.checkoutRequestID,
                  orderId: result.orderId,
                  formattedPhone: result.formattedPhone,
                  productUsed: testProduct.name,
                  mpesaReceipt: paymentData.payment.mpesaReceipt,
                  status: 'completed',
                });

                setStkTestHistory((prev) => [{
                  id: Date.now(),
                  timestamp: new Date().toLocaleString(),
                  phoneNumber: stkTestForm.phoneNumber,
                  amount: stkTestForm.amount,
                  checkoutRequestID: result.checkoutRequestID,
                  status: 'completed',
                  mpesaReceipt: paymentData.payment.mpesaReceipt,
                }, ...prev.slice(0, 9)]);

                showNotification('Payment received successfully!', 'success');
              }
            }
          } catch (error) {
            console.error('Error checking payment status:', error);
          }

          if (attempts >= maxAttempts && !paymentReceived) {
            clearInterval(pollInterval);
            setStkTestResult((prev) => ({
              ...prev,
              success: false,
              message: 'STK Push sent but no payment received within 30 seconds',
              status: 'timeout',
            }));
            showNotification('STK Push sent but no payment received. Please try again.', 'warning');
          }
        }, 1000);
      } else {
        setStkTestResult({ success: false, message: result.error || 'STK Push failed', error: result.error });
        showNotification(result.error || 'STK Push failed', 'error');
      }
    } catch (error) {
      console.error('STK Test error:', error);
      setStkTestResult({ success: false, message: 'Network error occurred', error: error.message });
      showNotification('Network error. Please try again.', 'error');
    } finally {
      setStkTestLoading(false);
    }
  };

  const testMpesaConnectivity = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/mpesa/test-connectivity`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.success) {
        showNotification('M-Pesa API connectivity test successful!', 'success');
      } else {
        showNotification(`M-Pesa connectivity test failed: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Connectivity test error:', error);
      showNotification('Connectivity test failed. Please try again.', 'error');
    }
  };

  return { stkTestForm, setStkTestForm, stkTestLoading, stkTestResult, stkTestHistory, handleStkTest, testMpesaConnectivity };
}
